import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, IconButton, LinearProgress, Slider, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { Close as CloseIcon, ExpandMore as ExpandMoreIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import {
  api, type FloorPlanCrop, type FloorPlanOrientationRow, type FloorPlanOrientationType, type FloorPlanOrientationsResponse, type TopViewingSitePlan,
} from '../../services/api';
import { normalizeDegrees } from '../../components/siteplan/solarPosition';
import NorthCalibrationDialog from '../../components/solar/NorthCalibrationDialog';
import SolarFloorPlan from '../../components/floorplan/SolarFloorPlan';
import { DEFAULT_WALL_HEIGHT_PERCENT } from '../../components/floorplan/floorPlanScene';
import { DEFAULT_WALL_THRESHOLD, WALL_THRESHOLD_RANGE } from '../../components/floorplan/floorPlanWalls';

// ---------------------------------------------------------------------------
// Manage Floor Plan North — admin-only dialog
//
// One row per unit type that has a floor plan image. Each type carries a
// type-default calibration (north, mirror, crop, wall height) plus optional
// per-block / per-stack overrides (north + mirror) for stacks that are rotated
// or mirrored relative to the drawn sheet. "Apply site plan north" seeds every
// uncalibrated type from the calibrated site plan; it never overwrites.
// ---------------------------------------------------------------------------

interface Props { open: boolean; onClose: () => void }

type OverrideDraft = { northAngle: number; mirror: boolean } | null; // null = inherit
const overrideKey = (block: string, stack: string) => `${block} ${stack}`;

const STATUS: Record<FloorPlanOrientationType['status'], { label: string; color: 'success' | 'warning' | 'info' | 'default' }> = {
  calibrated: { label: 'Calibrated', color: 'success' },
  estimated: { label: 'Estimated from site plan', color: 'info' },
  stale: { label: 'Image changed · recalibrate', color: 'warning' },
  missing: { label: 'Not calibrated', color: 'default' },
};

export default function ManageFloorPlanNorthDialog({ open, onClose }: Props) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [projectOptions, setProjectOptions] = useState<TopViewingSitePlan[]>([]);
  const [projectOptionsLoading, setProjectOptionsLoading] = useState(false);
  const [project, setProject] = useState<TopViewingSitePlan | null>(null);
  const [data, setData] = useState<FloorPlanOrientationsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Calibration dialog state
  const [editing, setEditing] = useState<FloorPlanOrientationType | null>(null);
  const [draftNorth, setDraftNorth] = useState(0);
  const [draftMirror, setDraftMirror] = useState(false);
  const [draftCrop, setDraftCrop] = useState<FloorPlanCrop | null>(null);
  const [draftWallHeight, setDraftWallHeight] = useState(DEFAULT_WALL_HEIGHT_PERCENT);
  const [draftThreshold, setDraftThreshold] = useState(DEFAULT_WALL_THRESHOLD);
  const [wallPieces, setWallPieces] = useState<number | null>(null);
  const [overrides, setOverrides] = useState<Map<string, OverrideDraft>>(new Map());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!open || projectOptions.length) return;
    setProjectOptionsLoading(true);
    api.getTopViewingSitePlans().then(setProjectOptions).catch(e => setError(e instanceof Error ? e.message : 'Unable to load projects'))
      .finally(() => setProjectOptionsLoading(false));
  }, [open, projectOptions.length]);

  const reload = useCallback(async (projectId: string) => {
    setLoading(true); setError(null);
    try { setData(await api.adminFloorPlanOrientations(projectId)); }
    catch (e) { setError(e instanceof Error ? e.message : 'Unable to load floor plan orientations'); setData(null); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (project?.projectId) reload(project.projectId); else setData(null); }, [project?.projectId, reload]);

  const summary = useMemo(() => {
    const types = data?.unitTypes ?? [];
    return { total: types.length, calibrated: types.filter(t => t.status === 'calibrated').length, estimated: types.filter(t => t.status === 'estimated').length,
      stale: types.filter(t => t.status === 'stale').length, missing: types.filter(t => t.status === 'missing').length };
  }, [data]);

  const applySitePlanNorth = async () => {
    if (!project) return;
    setApplying(true); setError(null); setNotice(null);
    try {
      const result = await api.applySitePlanNorthToFloorPlans(project.projectId);
      setNotice(`Applied ${result.sitePlanNorthAngle.toFixed(1)}° to ${result.applied} floor plan${result.applied === 1 ? '' : 's'} · ${result.skipped} already calibrated`);
      await reload(project.projectId);
    } catch (e) { setError(e instanceof Error ? e.message : 'Unable to apply site plan north'); }
    finally { setApplying(false); }
  };

  const openEditor = (type: FloorPlanOrientationType) => {
    const live = type.rows.filter(row => !row.stale);
    const typeRow = live.find(row => row.block === '' && row.stack === '') ?? null;
    setDraftNorth(typeRow?.northAngle ?? data?.sitePlanNorthAngle ?? 0);
    setDraftMirror(typeRow?.mirror ?? false);
    setDraftCrop(typeRow?.crop ?? null);
    setDraftWallHeight(typeRow?.wallHeight ?? DEFAULT_WALL_HEIGHT_PERCENT);
    setDraftThreshold(typeRow?.wallThreshold ?? DEFAULT_WALL_THRESHOLD);
    setWallPieces(null);
    const map = new Map<string, OverrideDraft>();
    live.filter(row => row.block !== '' || row.stack !== '').forEach(row => map.set(overrideKey(row.block, row.stack), { northAngle: row.northAngle, mirror: row.mirror }));
    setOverrides(map); setSaveError(''); setEditing(type);
  };

  const existingOverride = (type: FloorPlanOrientationType, block: string, stack: string): FloorPlanOrientationRow | undefined =>
    type.rows.find(row => !row.stale && row.block === block && row.stack === stack);

  const saveEditor = async () => {
    if (!editing || !project || !editing.imageUrl) return;
    setSaving(true); setSaveError('');
    try {
      await api.saveFloorPlanOrientation(project.projectId, { unitTypeId: editing.id, imageUrl: editing.imageUrl, northAngle: normalizeDegrees(draftNorth),
        mirror: draftMirror, crop: draftCrop, wallHeight: draftWallHeight, wallThreshold: draftThreshold });
      // Overrides: write changed / new ones, delete cleared ones.
      const keys = new Set<string>();
      editing.blocks.forEach(({ block, stacks }) => { keys.add(overrideKey(block, '')); stacks.forEach(stack => keys.add(overrideKey(block, stack))); });
      overrides.forEach((_, key) => keys.add(key));
      for (const key of Array.from(keys)) {
        const [block, stack] = key.split(' ');
        const draft = overrides.get(key) ?? null;
        const existing = existingOverride(editing, block, stack);
        if (draft) {
          if (!existing || existing.northAngle !== draft.northAngle || existing.mirror !== draft.mirror) {
            await api.saveFloorPlanOrientation(project.projectId, { unitTypeId: editing.id, imageUrl: editing.imageUrl, block, stack,
              northAngle: normalizeDegrees(draft.northAngle), mirror: draft.mirror });
          }
        } else if (existing) {
          await api.deleteFloorPlanOrientation(project.projectId, editing.id, block, stack);
        }
      }
      setEditing(null);
      await reload(project.projectId);
    } catch (e) { setSaveError(e instanceof Error ? e.message : 'Unable to save'); }
    finally { setSaving(false); }
  };

  const setOverride = (block: string, stack: string, value: OverrideDraft) => {
    setOverrides(prev => { const next = new Map(prev); if (value) next.set(overrideKey(block, stack), value); else next.delete(overrideKey(block, stack)); return next; });
  };

  const OverrideRow = ({ block, stack, label }: { block: string; stack: string; label: string }) => {
    const value = overrides.get(overrideKey(block, stack)) ?? null;
    return <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5, flexWrap: 'wrap' }}>
      <Typography sx={{ minWidth: 120, fontSize: 13 }}>{label}</Typography>
      <FormControlLabel sx={{ mr: 0 }} control={<Checkbox size="small" checked={value != null}
        onChange={(_, checked) => setOverride(block, stack, checked ? { northAngle: draftNorth, mirror: draftMirror } : null)} />}
        label={<Typography sx={{ fontSize: 12 }}>Own north</Typography>} />
      {value && <>
        <TextField size="small" type="number" label="North °" value={value.northAngle} inputProps={{ min: 0, max: 359.9, step: 0.1 }} sx={{ width: 110 }}
          onChange={e => setOverride(block, stack, { ...value, northAngle: Number(e.target.value) })} />
        <FormControlLabel sx={{ mr: 0 }} control={<Checkbox size="small" checked={value.mirror} onChange={(_, checked) => setOverride(block, stack, { ...value, mirror: checked })} />}
          label={<Typography sx={{ fontSize: 12 }}>Mirror</Typography>} />
        <Button size="small" onClick={() => setOverride(block, stack, { northAngle: normalizeDegrees(draftNorth), mirror: draftMirror })}>Use arrow</Button>
      </>}
      {!value && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>inherits {stack ? 'block / type' : 'type'} default</Typography>}
    </Stack>;
  };

  return <>
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={fullScreen}
      PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)', minHeight: '60vh' } }}>
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)', display: 'flex', alignItems: 'center', gap: 1 }}>
        Manage Floor Plan North
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ '& .MuiTableCell-root': { color: 'rgba(var(--color-text-rgb), 0.9)', borderColor: 'rgba(var(--color-text-rgb), 0.08)' } }}>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Autocomplete options={projectOptions} value={project} loading={projectOptionsLoading}
            onChange={(_, value) => { setProject(value); setNotice(null); setError(null); }}
            getOptionLabel={opt => opt.projectName || opt.projectId}
            isOptionEqualToValue={(a, b) => a.projectId === b.projectId}
            renderInput={params => <TextField {...params} label="Project" placeholder="Search a project" size="small" />} />
          {project && <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <Chip size="small" label={`${summary.total} floor plans`} />
            <Chip size="small" color="success" variant="outlined" label={`${summary.calibrated} calibrated`} />
            <Chip size="small" color="info" variant="outlined" label={`${summary.estimated} estimated`} />
            <Chip size="small" color="warning" variant="outlined" label={`${summary.stale} stale`} />
            <Chip size="small" variant="outlined" label={`${summary.missing} missing`} />
            <Chip size="small" variant="outlined" label={data?.sitePlanNorthAngle != null ? `Site plan north ${data.sitePlanNorthAngle.toFixed(1)}°` : 'Site plan north not calibrated'} />
            <Box sx={{ flex: 1 }} />
            <Tooltip title={data?.sitePlanNorthAngle == null ? 'Calibrate the site plan north first (Site Plan → 3DS → Set north)' : 'Seeds every uncalibrated floor plan with the site plan north. Never overwrites.'}>
              <span><Button size="small" variant="outlined" disabled={!data || data.sitePlanNorthAngle == null || applying || loading} onClick={applySitePlanNorth}>
                {applying ? 'Applying…' : 'Apply site plan north to all uncalibrated'}
              </Button></span>
            </Tooltip>
            <IconButton size="small" onClick={() => reload(project.projectId)} disabled={loading} aria-label="Reload"><RefreshIcon fontSize="small" /></IconButton>
          </Stack>}
          {loading && <LinearProgress />}
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
          {project && data && data.unitTypes.length === 0 && !loading && <Alert severity="info">This project has no unit types with a floor plan image.</Alert>}
          {data && data.unitTypes.length > 0 && <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Plan</TableCell><TableCell>Type</TableCell><TableCell align="right">Units</TableCell><TableCell>Blocks · stacks</TableCell>
                <TableCell>Status</TableCell><TableCell align="right">North</TableCell><TableCell align="right">Overrides</TableCell><TableCell />
              </TableRow></TableHead>
              <TableBody>
                {data.unitTypes.map(type => {
                  const typeRow = type.rows.find(row => row.block === '' && row.stack === '' && !row.stale);
                  const overrideCount = type.rows.filter(row => !row.stale && (row.block !== '' || row.stack !== '')).length;
                  return <TableRow key={type.id} hover>
                    <TableCell sx={{ width: 72 }}>{type.imageUrl && <Box component="img" src={type.imageUrl} alt="" sx={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 0.5, bgcolor: '#fff' }} />}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{type.unitType}</Typography>
                      <Typography sx={{ fontSize: 12, color: 'rgba(var(--color-text-rgb), 0.7)' }}>{[type.description, type.bedroom != null ? `${type.bedroom} BR` : null, type.size ? `${type.size} sqft` : null].filter(Boolean).join(' · ')}</Typography>
                    </TableCell>
                    <TableCell align="right">{type.unitCount}</TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography sx={{ fontSize: 12 }} noWrap title={type.blocks.map(b => `Blk ${b.block || '?'}: ${b.stacks.join(', ')}`).join(' · ')}>
                        {type.blocks.map(b => `Blk ${b.block || '?'} (${b.stacks.length})`).join(' · ') || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell><Chip size="small" color={STATUS[type.status].color} variant="outlined" label={STATUS[type.status].label} /></TableCell>
                    <TableCell align="right">{typeRow ? `${typeRow.northAngle.toFixed(1)}°${typeRow.mirror ? ' ⇋' : ''}` : type.status === 'estimated' && data.sitePlanNorthAngle != null ? `≈ ${data.sitePlanNorthAngle.toFixed(1)}°` : '—'}</TableCell>
                    <TableCell align="right">{overrideCount || '—'}</TableCell>
                    <TableCell align="right"><Button size="small" variant={typeRow ? 'text' : 'contained'} onClick={() => openEditor(type)} disabled={!type.imageUrl}>{typeRow ? 'Edit' : 'Calibrate'}</Button></TableCell>
                  </TableRow>;
                })}
              </TableBody>
            </Table>
          </Box>}
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>

    {editing && editing.imageUrl && <NorthCalibrationDialog open imageUrl={editing.imageUrl} maxWidth="xl"
      title={`Calibrate north · ${editing.unitType}${editing.description ? ` · ${editing.description}` : ''}`}
      description="Rotate the arrow to match N on the sheet or its key plan. Drag the dashed box to hug the drawn unit. The preview on the right shows the walls and shadows the agent will see."
      value={draftNorth} onChange={setDraftNorth} mirror={draftMirror} onMirrorChange={setDraftMirror}
      crop={draftCrop ?? { x: 0, y: 0, w: 100, h: 100 }} onCropChange={setDraftCrop}
      saving={saving} error={saveError} onCancel={() => { if (!saving) setEditing(null); }} onConfirm={saveEditor} confirmLabel="Save calibration"
      aside={<Box>
        <SolarFloorPlan unitId={0} imageUrl={editing.imageUrl} unitLabel={`${editing.unitType} · preview`} sunlight compact
          onFallback={() => undefined}
          preview={{ northAngle: normalizeDegrees(draftNorth), mirror: draftMirror, crop: draftCrop, wallHeightPercent: draftWallHeight,
            wallThreshold: draftThreshold, latitude: project?.mapLatitude ?? null, longitude: project?.mapLongitude ?? null }}
          onWalls={info => { setWallPieces(info.spans); if (!draftCrop && info.suggestedCrop) setDraftCrop(info.suggestedCrop); }} />
        <Box sx={{ px: 1, mt: 1 }}>
          <Typography sx={{ fontSize: 12 }}>Wall height · {draftWallHeight.toFixed(1)}% of plan width</Typography>
          <Slider min={2} max={20} step={0.5} value={draftWallHeight} onChange={(_, v) => setDraftWallHeight(v as number)} valueLabelDisplay="auto" />
          <Typography sx={{ fontSize: 12 }}>Wall detection · {draftThreshold}{wallPieces != null ? ` · ${wallPieces} wall pieces found` : ''}</Typography>
          <Slider min={WALL_THRESHOLD_RANGE[0]} max={WALL_THRESHOLD_RANGE[1]} step={5} value={draftThreshold} onChange={(_, v) => setDraftThreshold(v as number)} valueLabelDisplay="auto" />
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Lower if shading is picked up as walls · higher if faint walls are missed. The preview rebuilds as you drag.</Typography>
          {(project?.mapLatitude == null || project?.mapLongitude == null) && <Alert severity="info" sx={{ mt: 1 }}>Project map coordinates are missing, so the preview cannot place the sun.</Alert>}
        </Box>
      </Box>}>
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Block and stack overrides</Typography>
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
          Stacks that are rotated or mirrored relative to the drawn sheet get their own north. "Use arrow" copies the current arrow and mirror setting.
        </Typography>
        {editing.blocks.length === 0 && <Typography sx={{ fontSize: 12 }}>No units are assigned to this unit type.</Typography>}
        {editing.blocks.map(({ block, stacks }) => <Accordion key={block || '(no block)'} disableGutters defaultExpanded={editing.blocks.length <= 2}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Blk {block || '?'} · {stacks.length} stack{stacks.length === 1 ? '' : 's'}</Typography>
            {Array.from(overrides.keys()).some(key => key.startsWith(`${block} `)) && <Chip size="small" label="custom" sx={{ ml: 1 }} />}
          </AccordionSummary>
          <AccordionDetails>
            {block ? <>
              <OverrideRow block={block} stack="" label={`Whole block ${block}`} />
              {stacks.map(stack => <OverrideRow key={stack} block={block} stack={stack} label={`Stack ${stack}`} />)}
            </> : <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>These units have no block number, so they always use the type default.</Typography>}
          </AccordionDetails>
        </Accordion>)}
      </Box>
    </NorthCalibrationDialog>}
  </>;
}
